// src/utils/intake.js
//
// The print intake's pure rules: which files it accepts and how big, how a
// book's page count is estimated, and how the book specs open. They mirror what
// backend/routes/intake.py enforces. The server is the gate; these only let the
// page say no before an upload starts, and say it in the same words.

import { DEFAULT_SELECTIONS } from '../config/printDefaults';
import { bookPrintOption } from './printSizes';

const MB = 1024 * 1024;

// Mirrors MANUSCRIPT_TYPES, COVER_TYPES and the size limits in intake.py.
export const UPLOAD_RULES = {
  manuscript: {
    extensions: ['.pdf', '.docx', '.doc', '.rtf', '.odt', '.epub', '.txt', '.md', '.html', '.htm'],
    maxBytes: 50 * MB,
  },
  cover: {
    extensions: ['.pdf', '.jpg', '.jpeg', '.png', '.tif', '.tiff'],
    maxBytes: 50 * MB,
  },
};

export function extensionOf(name) {
  const value = String(name || '');
  const dot = value.lastIndexOf('.');
  return dot === -1 ? '' : value.slice(dot).toLowerCase();
}

/** Why `file` can't be uploaded as the `kind` file, or null when it can. */
export function fileProblem(file, kind) {
  const rule = UPLOAD_RULES[kind];
  if (!file) return `Add a ${kind} file.`;
  if (!rule.extensions.includes(extensionOf(file.name))) return `That ${kind} file type isn't accepted.`;
  if (file.size === 0) return `The ${kind} file is empty.`;
  if (file.size > rule.maxBytes) return `The ${kind} file is over ${rule.maxBytes / MB} MB.`;
  return null;
}

/** A PDF goes to review as it is. Any other manuscript is formatted by the team. */
export function isPrintReady(file) {
  return extensionOf(file && file.name) === '.pdf';
}

// What Import turns into a ReVisionari book (IMPORT_TYPES in
// backend/utils/manuscript_import.py).
export const IMPORT_EXTENSIONS = ['.docx', '.txt', '.md', '.html', '.htm', '.epub'];

export function canImport(file) {
  return IMPORT_EXTENSIONS.includes(extensionOf(file && file.name));
}

/** Older formats Import can't read yet: the author saves them as .docx first. */
export function needsResave(file) {
  return ['.doc', '.rtf', '.odt'].includes(extensionOf(file && file.name));
}

export function formatBytes(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < MB) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / MB).toFixed(1)} MB`;
}

export function money(amount) {
  return `$${Number(amount || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// The mock-up's estimate: about 1,800 characters of text to a printed page,
// held inside the pricing's page range.
const CHARS_PER_PAGE = 1800;

export function estimatePages(html, { min = 20, max = 1000 } = {}) {
  const text = String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&[a-z0-9#]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return Math.min(max, Math.max(min, Math.round(text.length / CHARS_PER_PAGE)));
}

// The same rule as EMAIL_RE in intake.py.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export function isValidEmail(value) {
  const email = String(value || '').trim();
  return email.length > 0 && email.length <= 254 && EMAIL_RE.test(email);
}

export const STATUS_LABEL = {
  under_review: 'Under review',
  changes_requested: 'Changes requested',
  approved: 'Approved',
};

/** A submission still holds its book while it is under review or sent back. */
export function isOpen(status) {
  return status === 'under_review' || status === 'changes_requested';
}

/**
 * The book specs' opening values.
 *
 * The /quote defaults, overlaid with what the author picked on /quote for this
 * book (`draft.print_spec.selections`), then fitted to the live pricing: an
 * option the pricing no longer offers falls back to its first option, the way
 * QuotePage does, and a number is held inside its range.
 */
export function openingSpecs(pricing, preferred) {
  const chosen = { ...DEFAULT_SELECTIONS, ...(preferred || {}) };
  const specs = {};
  ((pricing && pricing.categories) || []).forEach((cat) => {
    const value = chosen[cat.id];
    if (cat.type === 'number') {
      const n = Number(value);
      const whole = Number.isFinite(n) ? Math.round(n) : cat.min;
      specs[cat.id] = Math.min(cat.max, Math.max(cat.min, whole));
    } else if (cat.type === 'select') {
      const ids = (cat.options || []).map((o) => o.id);
      specs[cat.id] = ids.includes(value) ? value : ids[0];
    }
  });
  return specs;
}

/**
 * The specs a manuscript opens the in-app estimator at (Library → ⋮ → Book Pricing
 * Estimator), by the rule the specs step uses for a ReVisionari book: what was last
 * quoted or saved for it (`print_spec.selections`), at the size it is actually laid out
 * at, and with a page count estimated from its text when none was ever saved.
 */
export function specsForDraft(pricing, draft) {
  const preferred = { ...((draft && draft.print_spec && draft.print_spec.selections) || {}) };
  const own = bookPrintOption(draft);
  if (own) preferred.size = own;
  const pagesCat = ((pricing && pricing.categories) || []).find((c) => c.id === 'pages');
  if (preferred.pages == null && pagesCat) {
    preferred.pages = estimatePages(draft && draft.content, { min: pagesCat.min, max: pagesCat.max });
  }
  return openingSpecs(pricing, preferred);
}

/** The specs as label/value rows, the way the review step and the emails show them. */
export function specRows(pricing, specs) {
  return ((pricing && pricing.categories) || [])
    .filter((cat) => specs && specs[cat.id] !== undefined)
    .map((cat) => {
      const value = specs[cat.id];
      const option = (cat.options || []).find((o) => o.id === value);
      return { id: cat.id, label: cat.name, value: option ? option.name : String(value) };
    });
}

/** One key per submit attempt, so a retried request returns the first submission. */
export function newClientKey() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
